/**
 * @file SystemDesignViewer.jsx
 * @description Full-page, auth-gated viewer for individual System Design interview questions.
 *              Opens in a new tab from the SystemDesignPage sidebar.
 *              Features a sticky top toolbar, left-side heading TOC with scroll-spy,
 *              and the main Notion-rendered article. Dynamically sets the browser tab title.
 *
 * @module components/SystemDesignViewer
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost } from '../utils/apiUtils';
import {
    FiCheckCircle,
    FiCircle,
    FiRefreshCw,
    FiAlertCircle,
    FiArrowLeft,
    FiCheck,
    FiInstagram,
    FiYoutube
} from 'react-icons/fi';
import NotionRenderer from './NotionRenderer';
import Footer from './layout/Footer';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component SystemDesignViewer
 * @description Standalone viewer for a single System Design interview question.
 *              Fetches content metadata + Notion blocks, renders them with scroll-spy TOC,
 *              and provides mark-as-complete functionality for authenticated users.
 *
 * @param {Object} props
 * @param {string|number} props.contentId - The ID of the design content to display.
 * @param {Function} props.navigate - Router navigation function.
 *
 * @returns {JSX.Element} The full-page design viewer.
 */
const SystemDesignViewer = ({ contentId, navigate }) => {
    const { isAuthenticated, user } = useAuth();

    /** Smoothly scrolls the window back to the top (used by logo click). */
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // State: Content and Notion data
    const [content, setContent] = useState(null);
    const [notionData, setNotionData] = useState(null);

    // State: Loading and error
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State: Progress tracking
    const [isCompleted, setIsCompleted] = useState(false);
    const [markingComplete, setMarkingComplete] = useState(false);

    // State: Scroll-spy for TOC
    const [activeHeading, setActiveHeading] = useState(null);

    /* ── Data Fetching ── */

    /** Fetches the design content metadata (title, intro, notion_page_id) by contentId. */
    const fetchContent = async () => {
        try {
            const response = await apiGet(`/system-design/content/${contentId}`);

            if (response.ok) {
                const data = await response.json();
                setContent(data);
            } else if (response.status === 401) {
                const errorData = await response.json();
                setError(errorData.message || 'Please sign in to view this content');
                setTimeout(() => {
                    navigate('/signin');
                }, 2000);
            } else {
                throw new Error('Failed to fetch content');
            }
        } catch (err) {
            setError('Failed to load content details');
        }
    };

    /** Fetches the Notion-rendered blocks for the currently loaded content item. */
    const fetchNotionContent = async () => {
        if (!content) return;

        try {
            const response = await apiGet(`/system-design/notion/${content.notion_page_id}`);

            if (response.ok) {
                const data = await response.json();
                setNotionData(data);
            } else if (response.status === 401) {
                const errorData = await response.json();
                setError(errorData.message || 'Please sign in to view this content');
                setTimeout(() => {
                    navigate('/signin');
                }, 2000);
            } else {
                throw new Error('Failed to fetch Notion content');
            }
        } catch (err) {
            setError('Failed to load design content');
        }
    };

    /** Checks whether the authenticated user has already completed this design. */
    const checkCompletion = async () => {
        if (!isAuthenticated || !user?.id) return;

        try {
            const response = await apiGet(`/system-design/progress/${user.id}`);
            if (response.ok) {
                const progressMap = await response.json();
                setIsCompleted(progressMap[contentId]?.completed || false);
            }
        } catch (err) {
        }
    };

    /** Marks the current design as completed and updates local state. */
    const markAsComplete = async () => {
        if (!isAuthenticated || !user?.id) {
            alert('Please sign in to track your progress');
            return;
        }

        try {
            setMarkingComplete(true);
            const response = await apiPost('/system-design/complete', {
                contentId: parseInt(contentId)
            });

            if (response.ok) {
                setIsCompleted(true);
            } else {
                throw new Error('Failed to mark as complete');
            }
        } catch (err) {
            alert('Failed to save progress');
        } finally {
            setMarkingComplete(false);
        }
    };

    /** Smooth-scrolls to a heading element and sets it as active in the TOC. */
    const scrollToHeading = (headingId) => {
        const element = document.getElementById(`block-${headingId}`);
        if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });

            setActiveHeading(headingId);
        }
    };
    /* ── Effects ── */

    // Scroll-spy: highlights the active TOC heading based on scroll position
    useEffect(() => {
        const handleScroll = () => {
            if (!notionData?.headings) return;

            const scrollPosition = window.scrollY + 150;

            for (let i = notionData.headings.length - 1; i >= 0; i--) {
                const heading = notionData.headings[i];
                const element = document.getElementById(`block-${heading.id}`);

                if (element && element.offsetTop <= scrollPosition) {
                    setActiveHeading(heading.id);

                    const navButton = document.getElementById(`nav-${heading.id}`);
                    if (navButton) {
                        navButton.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                            inline: 'nearest'
                        });
                    }

                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [notionData]);

    // Primary data fetch: loads content metadata and checks completion status
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await fetchContent();
            await checkCompletion();
        };

        loadData();
    }, [contentId, isAuthenticated, user]);

    // Secondary fetch: loads Notion blocks once content metadata is available
    useEffect(() => {
        if (content) {
            fetchNotionContent().finally(() => setLoading(false));
        }
    }, [content]);

    // Dynamic page title: sets tab title to the design's name
    useEffect(() => {
        if (content?.title) {
            document.title = `${content.title} | SimpleCode`;
        }

        return () => {
            document.title = 'SimpleCode';
        };
    }, [content]);

    if (loading) {
        return <SystemDesignViewerSkeleton />;
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex items-center justify-center">
                <div className="text-center max-w-md mx-4 bg-white p-8 rounded border-l-4 border-emerald-600 ">
                    <FiAlertCircle className="text-4xl lg:text-5xl text-emerald-600 mx-auto mb-4" />
                    <h2 className="text-xl lg:text-2xl font-bold mb-2 text-foreground">Error Loading Design</h2>
                    <p className="text-sm lg:text-base text-slate-600 mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all duration-200 text-sm lg:text-base rounded  inline-flex items-center"
                    >
                        <FiRefreshCw className="mr-2 w-4 h-4" />
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "Learning", action: () => navigate("/learning") },
        ...(isAuthenticated ? [{ label: "Dashboard", action: () => navigate("/dashboard") }] : []),
        { label: "DSA Problems", action: () => navigate("/dsa-problems") }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans">
            <div className="fixed top-0 w-full bg-emerald-800 backdrop-blur-md border-b border-slate-200  z-50">
                <div className="max-w-8xl mx-auto px-6 sm:px-12 lg:px-16 py-2 flex items-center justify-between">
                    <button
                        onClick={() => navigate('/system-design')}
                        className="flex items-center space-x-2 text-gray-300 hover:text-white font-medium transition-colors duration-200 text-sm lg:text-base"
                    >
                        <FiArrowLeft className="w-4 h-4 lg:w-5 lg:h-5" />
                        <span className="hidden sm:inline">Back to System Design</span>
                        {/* <span className="sm:hidden">Back</span> */}
                    </button>

                    <img src="/logo.png" onClick={scrollToTop} alt="SimpleCode" className="h-8 lg:h-10 cursor-pointer" />

                    {isAuthenticated && (
                        <div className="flex items-center space-x-2 text-xs lg:text-sm font-medium">
                            {isCompleted ? (
                                <span className="flex items-center space-x-1 text-gray-300">
                                    <FiCheck className="w-4 h-4 lg:w-5 lg:h-5" />
                                    <span className="hidden sm:inline">Completed</span>
                                </span>
                            ) : (
                                <span className="flex items-center space-x-1 text-gray-300">
                                    <FiCircle className="w-4 h-4 lg:w-5 lg:h-5" />
                                    <span className="hidden sm:inline">In Progress</span>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <main className="flex-1 pt-24 pb-16">
                <div className="max-w-8xl mx-auto px-6 sm:px-12 lg:px-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-2 xl:gap-2">
                        <aside className="hidden lg:block lg:col-span-3 xl:col-span-3 sticky lg:top-24 self-start">
                            <div className="border border-border rounded bg-white overflow-hidden  flex flex-col" style={{ maxHeight: 'calc(100vh - 120px)' }}>
                                <div className="p-4 bg-emerald-800 border-b border-border  shrink-0">
                                    <h3 className="text-xs lg:text-sm font-semibold text-white uppercase tracking-wider flex items-center justify-center">
                                        Contents
                                    </h3>
                                </div>
                                <div className="overflow-y-auto flex-1 min-h-0 bg-white divide-y divide-border" style={{ overscrollBehavior: 'contain' }}>
                                    {notionData?.headings && notionData.headings.length > 0 ? (
                                        <div className="flex flex-col">
                                            {notionData.headings.map((heading) => (
                                                <button
                                                    key={heading.id}
                                                    id={`nav-${heading.id}`}
                                                    onClick={() => scrollToHeading(heading.id)}
                                                    className={`w-full text-left px-4 py-2 text-xs font-semibold lg:text-sm transition-all duration-200 border-l-4 group ${activeHeading === heading.id
                                                        ? 'border-emerald-800 text-emerald-700 bg-emerald-50/50'
                                                        : 'border-transparent text-slate-600 hover:text-emerald-700 hover:bg-slate-50'
                                                        }`}
                                                >
                                                    <div
                                                        className={`transition-all duration-200 ${activeHeading !== heading.id ? '' : ''}`}
                                                        style={{ paddingLeft: `${(heading.level - 1) * 12}px` }}
                                                    >
                                                        {heading.text}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-slate-400 text-xs lg:text-sm px-4 py-4 text-center">No headings found</p>
                                    )}
                                </div>
                            </div>
                        </aside>

                        <div className="lg:col-span-9 xl:col-span-9">
                            <article className="bg-white rounded  min-h-[85vh] border-l-4 border-emerald-600 relative overflow-hidden">
                                <div className="p-4 md:p-6 lg:p-6 mt-1">
                                    <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-8 text-left leading-tight">
                                        {content?.title}
                                    </h1>

                                    {notionData?.blocks && (
                                        <NotionRenderer blocks={notionData.blocks} />
                                    )}

                                    {isAuthenticated && !isCompleted && (
                                        <div className="mt-16 pt-8 border-t border-gray-200 flex justify-center">
                                            <button
                                                onClick={markAsComplete}
                                                disabled={markingComplete}
                                                className="px-2 py-1 text-muted-foreground hover:text-emerald-700 font-semibold rounded transition-all  flex items-center justify-center space-x-2 min-w-[200px]"
                                            >
                                                {markingComplete ? (
                                                    <>
                                                        <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                                                        <span>Saving...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>Mark as Completed</span>
                                                        <FiCheck className="w-5 h-5" />
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}

                                    {isCompleted && (
                                        <div className="mt-16 pt-8 border-t border-gray-200 flex justify-center">
                                            <div className="inline-flex items-center space-x-3 text-emerald-700">
                                                {/* <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white"> */}
                                                <FiCheck className="w-5 h-5 font-bold" />
                                                {/* </div> */}
                                                <span className="font-semibold text-lg">You've completed this design!</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </article>
                        </div>
                    </div>
                </div>
            </main>

            <Footer navigate={navigate} footerLinks={footerLinks} />
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * SKELETON LOADER
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component SystemDesignViewerSkeleton
 * @description Loading placeholder mimicking the viewer's toolbar, sidebar, and article layout.
 */
const SystemDesignViewerSkeleton = () => {
    return (
        <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans">
            {/* Nav Skeleton */}
            <div className="fixed top-0 w-full bg-emerald-800 border-b border-slate-200  z-50">
                <div className="max-w-8xl mx-auto px-6 sm:px-12 lg:px-16 py-3 flex items-center justify-between animate-pulse">
                    <div className="h-5 w-32 bg-emerald-700/50 rounded"></div>
                    <div className="h-8 w-24 bg-emerald-700/50 rounded opacity-60"></div>
                    <div className="h-5 w-24 bg-emerald-700/50 rounded"></div>
                </div>
            </div>

            <main className="flex-1 pt-24 pb-16">
                <div className="max-w-8xl mx-auto px-6 sm:px-12 lg:px-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-2 xl:gap-2">
                        {/* Sidebar Skeleton */}
                        <aside className="hidden lg:block lg:col-span-3 animate-pulse">
                            <div className="border border-border rounded bg-white overflow-hidden  flex flex-col" style={{ height: '400px' }}>
                                <div className="p-4 bg-emerald-800 border-b border-border ">
                                    <div className="h-4 w-1/2 bg-emerald-700/50 rounded mx-auto"></div>
                                </div>
                                <div className="p-4 space-y-4 bg-white">
                                    {[...Array(8)].map((_, i) => (
                                        <div key={i} className="h-4 w-full bg-slate-100 rounded"></div>
                                    ))}
                                </div>
                            </div>
                        </aside>

                        {/* Content Skeleton */}
                        <div className="lg:col-span-9 xl:col-span-9 animate-pulse">
                            <div className="bg-white rounded  min-h-[85vh] border-l-4 border-emerald-600 p-6 md:p-8 lg:p-10">
                                <div className="h-10 w-2/3 bg-slate-100 rounded mb-10"></div>
                                
                                <div className="space-y-6">
                                    <div className="space-y-3">
                                        <div className="h-4 w-full bg-slate-50 rounded"></div>
                                        <div className="h-4 w-full bg-slate-50 rounded"></div>
                                        <div className="h-4 w-4/5 bg-slate-50 rounded"></div>
                                    </div>

                                    <div className="h-72 w-full bg-slate-100 rounded-lg "></div>

                                    <div className="space-y-3">
                                        <div className="h-4 w-5/6 bg-slate-50 rounded"></div>
                                        <div className="h-4 w-full bg-slate-50 rounded"></div>
                                        <div className="h-4 w-3/4 bg-slate-50 rounded"></div>
                                    </div>
                                    
                                    <div className="h-48 w-full bg-slate-50 rounded-lg"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SystemDesignViewer;